import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Copy, Check, WhatsappLogo, ClockCounterClockwise, ChatCircleText, Image, Chat, User, HandCoins, MapPin, Tag, UsersThree, PaperPlaneTilt, IdentificationCard, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ClientFieldHistoryPanel from './ClientFieldHistoryPanel';
import MergeAccountsPanel from './MergeAccountsPanel';
import SendCredentialsModal from './SendCredentialsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hoisted to MODULE scope so its component identity stays STABLE across
// renders. Previously this was defined INSIDE EditClientModal, so every
// keystroke (which re-renders the modal) produced a brand-new SectionCard
// function reference — React treated it as a different component type and
// remounted the entire subtree, including the Client ID / Username inputs,
// stealing focus and dismissing the keyboard after each character. Pure
// (uses only its props), so it moves out verbatim.
const SectionCard = ({ icon: Icon, iconColor = 'text-gray-500', title, children, testId, className = '' }) => (
  <div className={`bg-gray-50 border border-gray-200 rounded-xl p-4 ${className}`} data-testid={testId}>
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
      {Icon && <Icon size={14} weight="bold" className={iconColor} />}
      {title}
    </h3>
    {children}
  </div>
);

const EditClientModal = ({ client, isRegistered, onClose, userId, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  // Which field's history panel is open. null = closed.
  //   { field: 'bank'|'upi'|'client_id'|'username'|'address', mode: 'audit'|'picker' }
  const [historyOpen, setHistoryOpen] = useState(null);
  // Live IFSC → branch address resolution (read-only). Refreshes any time the
  // handler edits the IFSC field OR when the modal first opens.
  const [ifscLookup, setIfscLookup] = useState(client.client_address || null);
  const [ifscLoading, setIfscLoading] = useState(false);

  // Point 39 — WhatsApp-style contact stats (Media / Messages / Chats) shown
  // in a compact row near the top of the profile. Read-only, best-effort.
  const [stats, setStats] = useState({ message_count: 0, media_count: 0, chat_count: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [showMerge, setShowMerge] = useState(false);
  // Point 6 — send DWS ID / Player ID credentials into the customer chat.
  // null = closed; otherwise { kind: 'dwsid'|'player'|'both', credentials: {...} }
  const [sendCred, setSendCred] = useState(null);
  // Point 8(iii) — real customer-facing DWS login link (for the DWS ID send).
  const [dwsLoginLink, setDwsLoginLink] = useState('');

  const DEFAULT_PASSWORD = 'Abcd1234';

  // Form state for moved clients
  const [formData, setFormData] = useState({
    serial_number: client.serial_number || '',
    client_id: client.client_id || '',
    player_password: client.player_password || '',
    username: client.username || '',
    mobile_number: client.mobile_number || client.phone || '',
    bank_account_number: client.bank_account_number || '',
    bank_name: client.bank_name || '',
    account_holder_name: client.account_holder_name || '',
    ifsc_code: client.ifsc_code || '',
    upi_id: client.upi_id || '',
    address: client.address || '',
    label_casino: client.label_casino || false,
    label_cricket: client.label_cricket || false,
    label_mix: client.label_mix || false,
    more_about_client: client.more_about_client || ''
  });
  
  // Login update state for registered clients
  const [loginData, setLoginData] = useState({
    username: client.username || '',
    phone: client.phone || '',
    password: ''
  });

  // Load linked accounts (same IP / multiple accounts)
  useEffect(() => {
    if (client.same_ip_flag && client.serial_number) {
      const loadLinked = async () => {
        try {
          const res = await axios.get(`${API}/clients/registered`);
          const linked = res.data.filter(
            c => c.serial_number === client.serial_number && c.id !== client.id
          );
          setLinkedAccounts(linked);
        } catch (err) {
          console.error('Failed to load linked accounts:', err);
        }
      };
      loadLinked();
    }
  }, [client.same_ip_flag, client.serial_number, client.id]);

  // Point 39 — load contact stats (Media / Messages / Chats) for this
  // customer. Best-effort: if the endpoint fails, the row stays at 0 rather
  // than blocking the rest of the profile.
  useEffect(() => {
    if (!client.id) { setStatsLoading(false); return; }
    let cancelled = false;
    setStatsLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customer/${client.id}/stats`);
        if (!cancelled && data) {
          setStats({
            message_count: data.message_count || 0,
            media_count: data.media_count || 0,
            chat_count: data.chat_count || 0,
          });
        }
      } catch (err) {
        console.error('Failed to load customer stats:', err);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client.id]);

  // Point 8(iii) — fetch the real customer-facing DWS login link so the
  // "Change DWS ID → Send to Chat" defaults to it (Player ID uses admin links).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        if (!cancelled && data) setDwsLoginLink(data.customer_login_link || data.web_link || '');
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced IFSC → branch address resolver. Fires 500ms after the handler
  // stops typing in the IFSC field. Purely display-only — the actual save
  // happens on the backend after Save Client is clicked.
  useEffect(() => {
    const code = (formData.ifsc_code || '').trim().toUpperCase();
    if (!code || code.length < 6) { setIfscLookup(null); return; }
    // If already resolved for this exact code, skip.
    if (ifscLookup && (ifscLookup.ifsc || '').toUpperCase() === code) return;
    let cancelled = false;
    setIfscLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/ifsc/${encodeURIComponent(code)}`);
        if (!cancelled) setIfscLookup(data);
      } catch {
        if (!cancelled) setIfscLookup(null);
      } finally {
        if (!cancelled) setIfscLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.ifsc_code]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    // Point 8(iv) — Player ID (client_id) must NOT allow spaces (typed or saved).
    if (name === 'client_id' && typeof v === 'string') v = v.replace(/\s/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: v
    }));
  };
  
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const copyToClipboard = async (text, fieldName) => {
    if (!text) {
      toast.error('No data to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const copyAllDetails = async () => {
    const formattedText = `Username..${formData.username || 'N/A'}
Mobile number.${formData.mobile_number || client.phone || 'N/A'}
Bank Account No: ${formData.bank_account_number || 'N/A'}
A/c Holder Name: ${formData.account_holder_name || 'N/A'}
IFSC Code: ${formData.ifsc_code || 'N/A'}
Bank Name: ${formData.bank_name || 'N/A'}
Address.. ${formData.address || 'N/A'}
UPI ID.. ${formData.upi_id || 'N/A'}
Amount..`;

    try {
      await navigator.clipboard.writeText(formattedText);
      toast.success('Details copied!');
    } catch (err) {
      toast.error('Failed to copy details');
    }
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = () => {
    const phone = formData.mobile_number || client.phone;
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Update client details
      await axios.put(`${API}/clients/update/${client.id}`, formData, {
        params: { 
          user_id: userId,
          is_registered: isRegistered
        }
      });
      toast.success('Client details updated!');
      
      setLoading(false);
      onClose();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to update client');
      setLoading(false);
    }
  };
  
  const handleLoginUpdate = async () => {
    if (!loginData.password) {
      toast.error('Please enter a new password');
      return;
    }
    
    try {
      setLoading(true);
      
      await axios.put(`${API}/clients/update-login/${client.id}`, loginData);
      toast.success('Login credentials updated successfully!');
      
      setLoading(false);
      setLoginData({ ...loginData, password: '' });
    } catch (error) {
      console.error('Failed to update login:', error);
      toast.error(error.response?.data?.detail || 'Failed to update login');
      setLoading(false);
    }
  };

  // Point 6 — Change DWS ID (username) + login mobile + password in one go.
  // Any subset can change; at least one must be provided.
  const handleDwsidUpdate = async () => {
    const uname = (loginData.username || '').trim();
    const phone = (loginData.phone || '').trim();
    const pwd = (loginData.password || '').trim();
    if (!uname && !phone && !pwd) {
      toast.error('Change at least one field (DWS ID, mobile or password)');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/clients/${client.id}/change-dwsid`, {
        actor_id: userId,
        username: uname,
        mobile_number: phone,
        password: pwd,
      });
      toast.success('DWS ID / login credentials updated!');
      setLoginData((p) => ({ ...p, password: '' }));
      // Reflect the new DWS ID / phone in the top form too.
      setFormData((p) => ({ ...p, username: uname || p.username, mobile_number: phone || p.mobile_number }));
      setLoading(false);
    } catch (error) {
      console.error('Failed to change DWS ID:', error);
      toast.error(error.response?.data?.detail || 'Failed to change DWS ID');
      setLoading(false);
    }
  };
  
  const CopyButton = ({ text, fieldName }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, fieldName)}
      className="p-1.5 hover:bg-gray-100 rounded transition-colors"
      disabled={!text}
    >
      {copiedField === fieldName ? (
        <Check size={18} weight="bold" className="text-green-600" />
      ) : (
        <Copy size={18} weight="bold" className={text ? 'text-gray-600' : 'text-gray-300'} />
      )}
    </button>
  );

  // Compact "H" chip — opens the field-history audit modal for any of the 5
  // mandatory fields. Refreshes the form once a historical value is selected
  // so the user sees the reverted value immediately.
  const HistoryChip = ({ field, mode = 'audit', label = 'H' }) => (
    <button
      type="button"
      onClick={() => setHistoryOpen({ field, mode })}
      className="inline-flex items-center justify-center w-5 h-5 rounded bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-black shadow-sm"
      title={`View ${field} change history`}
      data-testid={`edit-client-history-btn-${field}`}
    >
      {label === 'H' ? 'H' : <ClockCounterClockwise size={11} weight="bold" />}
    </button>
  );

  // Point 39 — compact WhatsApp contact-profile style stat tile ("142 · Messages").
  const StatTile = ({ icon: Icon, value, label, testId }) => (
    <div
      className="flex-1 bg-white border border-gray-200 rounded-xl py-2.5 flex flex-col items-center gap-0.5 shadow-sm"
      data-testid={testId}
    >
      <Icon size={16} weight="bold" className="text-[#25D366]" />
      <div className="text-sm font-bold text-gray-800 leading-none mt-0.5">
        {statsLoading ? '—' : value}
      </div>
      <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">{label}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {showMerge && (
        <MergeAccountsPanel
          client={client}
          userId={userId}
          onClose={() => setShowMerge(false)}
          onMerged={() => { setShowMerge(false); try { onClose && onClose(); } catch (_) {} }}
        />
      )}
      {sendCred && (
        <SendCredentialsModal
          client={client}
          userId={userId}
          kind={sendCred.kind}
          credentials={sendCred.credentials}
          defaultLink={dwsLoginLink}
          onClose={() => setSendCred(null)}
        />
      )}
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#25D366] text-white p-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-lg font-bold">
            {isRegistered ? 'Edit Registered Client' : 'Edit Client Details'}
          </h2>
          <div className="flex items-center gap-1">
            {/* Phase D — Merge duplicates / New User chats into this account.
                Only for a real customer profile (merge target must be real). */}
            {client?.id && client?.role === 'customer' && !client?.is_guest && (
              <button
                type="button"
                onClick={() => setShowMerge(true)}
                className="flex items-center gap-1 text-xs font-semibold bg-white/20 hover:bg-white/30 px-2.5 py-1.5 rounded-lg"
                data-testid="open-merge-btn"
                title="Merge New User / duplicate chats into this client"
              >
                <UsersThree size={16} weight="bold" /> Merge
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-green-600 rounded-full"
            >
              <X size={24} weight="bold" />
            </button>
          </div>
        </div>
        
        {/* Point 39 — WhatsApp contact-profile style stats row: Messages / Media / Chats */}
        <div className="px-4 pt-4 pb-1 flex gap-2" data-testid="edit-client-stats-row">
          <StatTile icon={ChatCircleText} value={stats.message_count} label="Messages" testId="edit-client-stat-messages" />
          <StatTile icon={Image} value={stats.media_count} label="Media" testId="edit-client-stat-media" />
          <StatTile icon={Chat} value={stats.chat_count} label="Chats" testId="edit-client-stat-chats" />
        </div>

        {/* Form Content */}
        <div className="p-6 pt-3 space-y-4">
          {/* Basic Info / Identity Section */}
          <SectionCard icon={User} iconColor="text-indigo-500" title="Basic Information">
            {/* Client Code - Non-editable (Session-48g rename) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  placeholder="Auto-generated"
                />
                <CopyButton text={formData.serial_number} fieldName="serial_number" />
              </div>
            </div>

            {/* Joining Date - Non-editable */}
            {client.created_at && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Joining Date
                </label>
                <input
                  type="text"
                  value={new Date(client.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  data-testid="joining-date"
                />
              </div>
            )}
            
            {/* Player ID (formerly "Client ID") — save/send only field, no
                system-login meaning. Renamed per Point 6. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <span>Player ID <span className="text-[10px] text-gray-400 uppercase">(save / send only)</span></span>
                <HistoryChip field="client_id" />
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter player ID"
                  data-testid="edit-client-client-id-input"
                />
                <CopyButton text={formData.client_id} fieldName="client_id" />
              </div>
            </div>

            {/* Player Password — the Player ID's own password (Point 6).
                Purely for saving & sending to the customer; unrelated to login. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Player Password <span className="text-[10px] text-gray-400 uppercase">(for Player ID)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="player_password"
                  autoComplete="off"
                  value={formData.player_password}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter player password"
                  data-testid="edit-client-player-password-input"
                />
                <button
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, player_password: DEFAULT_PASSWORD }))}
                  className="px-2.5 py-2 text-xs font-bold rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200 whitespace-nowrap"
                  data-testid="player-password-default-btn"
                  title={`Set default password (${DEFAULT_PASSWORD})`}
                >
                  Default
                </button>
                <CopyButton text={formData.player_password} fieldName="player_password" />
              </div>
            </div>
            
            {/* Username = DWS ID (login identifier) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <span>Username <span className="text-[10px] text-emerald-600 uppercase font-bold">(DWS ID · login)</span></span>
                <HistoryChip field="username" />
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="username"
                  autoComplete="off"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter username"
                />
                <CopyButton text={formData.username} fieldName="username" />
              </div>
            </div>
            
            {/* Mobile Number — editable by BOTH admin and handler (Point 5).
                The mobile number is the customer's login identifier: whichever
                value is saved here becomes the primary and updates system-wide. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <span>Mobile Number <span className="text-[10px] text-gray-400 uppercase">(login)</span></span>
                <HistoryChip field="mobile_number" />
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter mobile number"
                  data-testid="edit-client-mobile-input"
                />
                <CopyButton text={formData.mobile_number} fieldName="mobile_number" />
              </div>
            </div>
          </SectionCard>

          {/* Bank & Payment Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <HandCoins size={14} weight="bold" className="text-emerald-600" />
                Bank & Payment Details
              </h3>
              <button
                type="button"
                onClick={() => setHistoryOpen({ field: 'bank', mode: 'picker' })}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50 transition"
                data-testid="edit-client-bank-history-link"
                title="View all bank accounts on record — pick the active one"
              >
                <ClockCounterClockwise size={12} weight="bold" /> History
              </button>
            </div>

            {/* Bank Account Number */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <span>Bank Account Number</span>
                <HistoryChip field="bank" />
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter account number"
                />
                <CopyButton text={formData.bank_account_number} fieldName="bank_account_number" />
              </div>
            </div>
            
            {/* Bank Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter bank name"
                />
                <CopyButton text={formData.bank_name} fieldName="bank_name" />
              </div>
            </div>
            
            {/* Account Holder Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder Name
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter account holder name"
                />
                <CopyButton text={formData.account_holder_name} fieldName="account_holder_name" />
              </div>
            </div>
            
            {/* IFSC Code */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IFSC Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter IFSC code"
                />
                <CopyButton text={formData.ifsc_code} fieldName="ifsc_code" />
              </div>
            </div>

            {/* IFSC-derived Client Address (READ-ONLY, auto-fetched from Razorpay IFSC API) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <span>📍 Client Address (from IFSC)</span>
                {ifscLoading && <span className="text-[10px] text-blue-500 animate-pulse">Fetching…</span>}
              </label>
              <div className="flex gap-2 items-start">
                <div
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm min-h-[42px] whitespace-pre-line ${ifscLookup ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                  data-testid="edit-client-ifsc-address"
                  title="Auto-derived from IFSC code. Cannot be edited directly."
                >
                  {ifscLookup ? (
                    <>
                      <div className="font-semibold">{ifscLookup.bank} · {ifscLookup.branch}</div>
                      <div className="text-xs">{ifscLookup.address}</div>
                      <div className="text-[11px] mt-0.5 opacity-80">
                        {[ifscLookup.city, ifscLookup.district, ifscLookup.state].filter(Boolean).join(' · ')}
                      </div>
                    </>
                  ) : (
                    <span className="italic">Enter a valid IFSC code above to auto-fetch the branch address.</span>
                  )}
                </div>
                {ifscLookup && <CopyButton text={ifscLookup.formatted || ifscLookup.address} fieldName="client_address" />}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Auto-updates whenever the IFSC changes. Read-only.</p>
            </div>

            {/* UPI ID */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <span>UPI ID</span>
                  <HistoryChip field="upi" />
                </label>
                <button
                  type="button"
                  onClick={() => setHistoryOpen({ field: 'upi', mode: 'picker' })}
                  className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 px-1.5 py-0.5 rounded border border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50 flex items-center gap-1"
                  data-testid="edit-client-upi-history-link"
                >
                  <ClockCounterClockwise size={10} weight="bold" /> History
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="upi_id"
                  value={formData.upi_id}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter UPI ID (e.g., user@paytm)"
                />
                <CopyButton text={formData.upi_id} fieldName="upi_id" />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <SectionCard icon={MapPin} iconColor="text-blue-500" title="Address">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                <span>Address</span>
                <HistoryChip field="address" />
              </label>
              <div className="flex gap-2">
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none bg-white"
                  placeholder="Enter address"
                  rows={2}
                />
                <CopyButton text={formData.address} fieldName="address" />
              </div>
            </div>
          </SectionCard>

          {/* Copy All & WhatsApp Buttons — right-side copy button keeps its
              existing copy logic/onClick untouched; only a small caption
              label was added directly below it (Point 39, Part A). */}
          <div>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <button
                  type="button"
                  onClick={copyAllDetails}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Copy size={20} weight="bold" />
                  Copy All Details
                </button>
                {/* Point 39, Part A — small muted label directly below the copy button.
                    Does not touch copyAllDetails / onClick — display-only caption. */}
                <p className="text-[10px] text-gray-500 mt-1 text-center" data-testid="edit-client-copy-all-caption">
                  Bank Account Details
                </p>
              </div>
              <button
                type="button"
                onClick={openWhatsApp}
                className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <WhatsappLogo size={20} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Copies: Username, Bank Details, Address, UPI
            </p>
          </div>

          {/* Labels & Notes Section */}
          <SectionCard icon={Tag} iconColor="text-purple-500" title="Labels & Notes">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_casino"
                  checked={formData.label_casino}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">Casino</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_cricket"
                  checked={formData.label_cricket}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">Cricket</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_mix"
                  checked={formData.label_mix}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">Mix</span>
              </label>
            </div>

            {/* More About Client */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                More about client
              </label>
              <div className="flex gap-2">
                <textarea
                  name="more_about_client"
                  value={formData.more_about_client}
                  onChange={handleInputChange}
                  rows={3}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none bg-white"
                  placeholder="Additional notes about the client..."
                />
                <CopyButton text={formData.more_about_client} fieldName="more_about_client" />
              </div>
            </div>
          </SectionCard>

          {/* Credentials Section (Only for Registered Clients) — split into
              "Change DWS ID (login)" and "Player ID", each with a
              Send-to-Chat action (Point 6). */}
          {isRegistered && (
            <>
              {/* ===== Change DWS ID (actual login credentials) ===== */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="change-dwsid-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide flex items-center gap-1.5">
                    <IdentificationCard size={15} weight="bold" /> Change DWS ID (Login)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSendCred({ kind: 'dwsid', credentials: {
                      username: loginData.username,
                      mobile_number: loginData.phone,
                      password: loginData.password,
                    } })}
                    className="text-[11px] font-bold text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 rounded-lg px-2 py-1 flex items-center gap-1 transition-colors"
                    data-testid="send-dwsid-btn"
                    title="Send DWS ID + login to the customer chat"
                  >
                    <PaperPlaneTilt size={13} weight="bold" /> Send to Chat
                  </button>
                </div>
                <p className="text-xs text-gray-600 mb-4">
                  ⚠️ DWS ID + mobile + password se customer login karta hai. Change karne par uski saari sessions logout ho jaayengi.
                </p>

                {/* DWS ID (Username) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">DWS ID (Username)</label>
                  <input
                    type="text"
                    name="username"
                    autoComplete="off"
                    value={loginData.username}
                    onChange={handleLoginChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Enter DWS ID / username"
                    data-testid="dwsid-username-input"
                  />
                </div>

                {/* Mobile Number (login) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number (login)</label>
                  <input
                    type="text"
                    name="phone"
                    value={loginData.phone}
                    onChange={handleLoginChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Enter mobile number"
                    data-testid="dwsid-mobile-input"
                  />
                </div>

                {/* New Password */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="password"
                      autoComplete="off"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Leave blank to keep current password"
                      data-testid="dwsid-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setLoginData((p) => ({ ...p, password: DEFAULT_PASSWORD }))}
                      className="px-2.5 py-2 text-xs font-bold rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200 whitespace-nowrap"
                      data-testid="dwsid-password-default-btn"
                      title={`Set default password (${DEFAULT_PASSWORD})`}
                    >
                      Default
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleDwsidUpdate}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  data-testid="dwsid-update-btn"
                >
                  {loading ? 'Updating…' : 'Update DWS ID / Login'}
                </button>
              </div>

              {/* ===== Player ID credentials (send only) ===== */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="player-id-section">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Key size={15} weight="bold" /> Player ID
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSendCred({ kind: 'player', credentials: {
                      player_id: formData.client_id,
                      player_password: formData.player_password,
                    } })}
                    className="text-[11px] font-bold text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 rounded-lg px-2 py-1 flex items-center gap-1 transition-colors"
                    data-testid="send-player-id-btn"
                    title="Send Player ID + password to the customer chat"
                  >
                    <PaperPlaneTilt size={13} weight="bold" /> Send to Chat
                  </button>
                </div>
                <p className="text-xs text-gray-600">
                  Player ID: <span className="font-semibold text-gray-800">{formData.client_id || '—'}</span>
                  {formData.player_password ? <> · Password: <span className="font-semibold text-gray-800">{formData.player_password}</span></> : null}
                </p>
                <p className="text-[11px] text-gray-500 mt-1">Player ID / password ko upar "Basic Information" me edit karke Save Changes karein. Ye sirf save & send ke liye hai.</p>
              </div>

              {/* Point 8(ii) — Send BOTH credential sets in one chat message */}
              <button
                type="button"
                onClick={() => setSendCred({ kind: 'both', credentials: {
                  username: loginData.username,
                  mobile_number: loginData.phone,
                  password: loginData.password,
                  player_id: formData.client_id,
                  player_password: formData.player_password,
                } })}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold flex items-center justify-center gap-2"
                data-testid="send-both-btn"
              >
                <PaperPlaneTilt size={16} weight="bold" /> Send Both (DWS ID + Player ID) to Chat
              </button>
            </>
          )}

          {/* Linked Accounts (Multiple Account / Same IP) */}
          {linkedAccounts.length > 0 && (
            <div className="bg-orange-50/40 border border-orange-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-orange-700 mb-3 uppercase tracking-wide">
                Linked Accounts (Multiple Account)
              </h3>
              {linkedAccounts.map((linked) => (
                <div key={linked.id} className="bg-orange-50 rounded-lg p-3 mb-2 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-700 font-bold text-xs">{(linked.username || '?')[0].toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{linked.username || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{linked.phone || 'No phone'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                    <div>Client Code: {linked.serial_number || 'N/A'}</div>
                    <div>ID: {linked.client_id || 'N/A'}</div>
                    {linked.bank_name && <div>Bank: {linked.bank_name}</div>}
                    {linked.account_holder_name && <div>A/c: {linked.account_holder_name}</div>}
                    {linked.bank_account_number && <div>No: {linked.bank_account_number}</div>}
                    {linked.ifsc_code && <div>IFSC: {linked.ifsc_code}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 p-4 flex gap-3 rounded-b-lg border-t">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Field-history modal — opens for any of the 5 mandatory fields */}
      {historyOpen && (
        <ClientFieldHistoryPanel
          customerId={client.id}
          field={historyOpen.field}
          mode={historyOpen.mode}
          actorId={userId}
          actorRole={userRole}
          currentValue={formData}
          onClose={() => setHistoryOpen(null)}
          onSelected={(entry) => {
            // Mirror picked value into the form so user sees it right away
            const v = entry?.value || {};
            if (historyOpen.field === 'bank') {
              setFormData((p) => ({
                ...p,
                account_holder_name: v.account_holder_name ?? p.account_holder_name,
                bank_account_number: v.bank_account_number ?? p.bank_account_number,
                ifsc_code: v.ifsc_code ?? p.ifsc_code,
                bank_name: v.bank_name ?? p.bank_name,
              }));
            } else if (historyOpen.field === 'upi') {
              setFormData((p) => ({ ...p, upi_id: v.upi_id ?? p.upi_id }));
            } else if (historyOpen.field === 'client_id') {
              setFormData((p) => ({ ...p, client_id: v.text ?? p.client_id }));
            } else if (historyOpen.field === 'username') {
              setFormData((p) => ({ ...p, username: v.text ?? p.username }));
            } else if (historyOpen.field === 'address') {
              setFormData((p) => ({ ...p, address: v.text ?? p.address }));
            } else if (historyOpen.field === 'mobile_number') {
              setFormData((p) => ({ ...p, mobile_number: v.text ?? p.mobile_number }));
            }
          }}
        />
      )}
    </div>
  );
};

export default EditClientModal;
